//angular imports
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  resource,
  Signal,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, Subject, takeUntil } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";

//local imports
import {
  CatalogConstants,
  CatalogDataChangedMessage,
  CatalogStateService,
  CategoryModel,
  DocumentModel,
  HidePrintRoomPropertiesMessage,
  PrintRoomPropertiesMessage,
  PropertyType,
  ShowPrintRoomPropertiesMessage,
} from "@app/catalog";
import { CatalogCommonConstants } from "@app/catalog-common";
import { ApplicationSecurity, SearchModel, SecurityConfig } from "@app/core";
import {
  BreadcrumbChangedMessage,
  BreadcrumbItemToggleMessage,
  IBreadcrumbItemModel,
  Messenger,
  UnityConfig,
  UnityParams,
  UnitySecurity,
} from "@app/shared";
import { CommonConstants } from "@app/app-common";

@Component({
  selector: "room-property",
  standalone: false,
  templateUrl: "./room-property.component.html",
  styleUrl: "./room-property.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomPropertyComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private catalogStateService = inject(CatalogStateService);
  private readonly destroying$ = new Subject<void>();
  private selectedCategoryId = signal<string>(undefined);  
  private currentBreadcrumbs: IBreadcrumbItemModel[] = [];


  public propertyType = PropertyType;
  public catalogMenuChanged: Signal<CatalogDataChangedMessage>;

  public readonly is3DViewerEnable = signal(false);
  public catalogConstants = CatalogConstants;

  public menuLoaded = computed(() => {
    let msg = this.catalogMenuChanged();
    return !!msg;
  });

  public roomId = toSignal(
     this.route.paramMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.ID)))
  );

  private departmentId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.DEPARTMENT_ID)))
  );

  private programId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.PROGRAM_ID)))
  );

  private projectId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.PROJECT_ID)))
  );


  public filteredCategories = computed(() => {
      const selectedId = this.selectedCategoryId();
      const categories = this.roomPropertyResource.value()?.categories() ?? [];

      return selectedId
        ? categories.filter(cat => cat.id === selectedId)
        : categories;
    });


  public roomPropertyResource = resource({
    request: () => ({
      id: this.roomId(),
      departmentId: this.departmentId(),
      programId: this.programId(),
      projectId: this.projectId(),
    }),
    loader: async ({ request }) => {
      const property = await this.catalogStateService.getRoomProperty(
        request.id,
        request.departmentId,
        request.programId,
        request.projectId,
      );

      if (property.documents) {
        const filteredDocuments = property.documents().filter(
          (doc: DocumentModel) => !doc.superseded()
        );
        property.documents.set(filteredDocuments);
      }
          
      return property;
    }
  });

  public RoomPropertyComponent: typeof RoomPropertyComponent = RoomPropertyComponent;


  public roomContents = computed(() => {
      const categories = this.roomPropertyResource.value().categories();
      const filteredCategories = this.selectedCategoryId() 
          ? categories.filter(cat => cat.id === this.selectedCategoryId())
          : categories;
      
      return filteredCategories
          .flatMap(cat => cat.roomContents())
          .sort((a, b) => {
            const sortKeyA = a.code() || a.name();
            const sortKeyB = b.code() || b.name();
            return sortKeyA.localeCompare(sortKeyB);
          });
  });
  
  public static cacheContext: any = {};

  public config = computed(() => {
    let c = new UnityConfig();

    c.dataUrl = "/assets/partviewer/Build/part-viewer.data.gz";
    c.frameworkUrl = "/assets/partviewer/Build/part-viewer.framework.js.gz";
    c.codeUrl = "/assets/partviewer/Build/part-viewer.wasm.gz";
    c.streamingAssetsUrl = "/assets/partviewer/StreamingAssets";
    c.productName = "Modutecture Part Viewer";
    return c;
  });

  public security = computed(() => {
    return {
      getProtectedResources: () => this.applicationSecurity.getProtectedResources(),
      applicationUri: this.securityConfig.applicationUri,
      appId: this.securityConfig.appId,
      azureB2c: {
          instance: this.securityConfig.azureB2c.instance,
          domain: this.securityConfig.azureB2c.domain
      }
    } as UnitySecurity });

  public params: Signal<UnityParams[]> = computed(() => {
    return [ 
      { key: "SetPartId", value: this.roomPropertyResource.value().partId() ?? ""  },
      { key: "SetComponentId", value: this.roomPropertyResource.value().componentId() ?? ""  } ];
  });

  constructor(
    private applicationSecurity: ApplicationSecurity,
    private securityConfig: SecurityConfig,
    private messenger: Messenger
  ) {
      this.catalogMenuChanged = this.messenger.getSignal(CatalogDataChangedMessage);

      this.messenger.subscribe(this, PrintRoomPropertiesMessage, async (msg) => {
        if (msg.handled) return;
        msg.handled = true;
        await this.printRoomProperties();
      });

      this.messenger.subscribe(this, BreadcrumbChangedMessage, (msg) => {
        if(msg.breadcrumbName !== CommonConstants.CATALOG) return;
        this.currentBreadcrumbs = msg.breadcrumbs;
      });
  }


  public ngOnInit(): void {
    this.catalogStateService.initialize(this.route);

    this.messenger.send(new ShowPrintRoomPropertiesMessage());


    this.route.queryParamMap.pipe(
      takeUntil(this.destroying$)
    ).subscribe((params) => {
      const roomId = params.get(CatalogCommonConstants.ROUTE_PARAMS.ROOM_ID);

      this.selectedCategoryId.set(params.get(CatalogCommonConstants.ROUTE_PARAMS.CATEGORY_ID));

      if (!roomId) {
        this.navigateToListPage();
      }

    });

    this.removeSearchBreadcrumbs();
  }


  public navigateToListPage() {
    this.router.navigate(["/catalog"], {
      queryParamsHandling: "merge",
    });
  }

  public open3DViewer(): void {
    this.is3DViewerEnable.set(true);
  }

  public close3DViewer(): void {
    this.is3DViewerEnable.set(false);
  }

  public onCategorySelected(category: CategoryModel) {
      this.selectedCategoryId.set(category.id);
      this.messenger.send(new BreadcrumbItemToggleMessage(category, CatalogConstants.CATALOG));
  }  

  private async printRoomProperties(): Promise<void> {
    await this.catalogStateService.printRoomProperties(this.roomId());
  }


  private removeSearchBreadcrumbs() {
    this.currentBreadcrumbs.forEach(cb => {
      if(cb instanceof SearchModel) {
        this.messenger.send(new BreadcrumbItemToggleMessage(cb, CatalogConstants.CATALOG));
      }
    });
  }

  ngOnDestroy(): void {
    this.messenger.send(new HidePrintRoomPropertiesMessage());
    this.messenger.unsubscribeByOwner(this);
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }
}
